import cx from '@vindr/utils/helpers/cx';
import debounce from 'lodash/debounce';
import { CSSProperties, FC, MouseEvent, ReactNode, useEffect, useRef, useState } from 'react';
import './Popup.css';

interface PopupProps {
  visible: boolean;
  mask?: boolean;
  showCloseButton?: boolean;
  closeOnMaskClick?: boolean;
  onClose?: () => void;
  onMaskClick?: (event: MouseEvent<HTMLDivElement, MouseEvent>) => void;
  children: ReactNode;
  wrapperClassName?: Parameters<typeof cx>[0];
  bodyClassName?: Parameters<typeof cx>[0];
  style?: CSSProperties;
  bodyStyle?: CSSProperties;
}

const classPrefix = `vindr-popup`;

const Popup: FC<PopupProps> = ({
  visible,
  mask = true,
  showCloseButton = false,
  closeOnMaskClick = true,
  onClose,
  onMaskClick,
  children,
  wrapperClassName,
  bodyClassName,
  style,
  bodyStyle,
}) => {
  const [active, setActive] = useState(visible);
  const ref = useRef<HTMLDivElement>(null);

  const debouncedOnClose = debounce(onClose, 300);

  useEffect(() => {
    if (visible) {
      setActive(true);
    } else {
      setTimeout(() => {
        setActive(false);
      }, 300);
    }
  }, [visible, onClose]);

  useEffect(() => {
    const handleClickOutside = (event: globalThis.MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        debouncedOnClose?.();
      }
    };

    if (!mask) {
      if (active) {
        document.addEventListener('mousedown', handleClickOutside);
      } else {
        document.removeEventListener('mousedown', handleClickOutside);
      }
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [active, mask, debouncedOnClose]);

  if (!active) return null;

  return (
    <div
      className={cx(`${classPrefix} `, { 'vindr-popup-closing': !visible }, wrapperClassName)}
      onClick={debouncedOnClose}
      {...{ ref, style }}
    >
      {mask && (
        <div
          className={`${classPrefix}-mask`}
          style={{ display: active ? 'block' : 'none' }}
          onClick={(e) => {
            onMaskClick?.(e);
            if (closeOnMaskClick) {
              debouncedOnClose?.();
            }
          }}
        />
      )}
      <div
        className={cx(`${classPrefix}-body`, bodyClassName)}
        onClick={handleChildClick}
        style={bodyStyle}
      >
        {showCloseButton && (
          <a
            className={`${classPrefix}-close-icon adm-plain-anchor`}
            onClick={debouncedOnClose}
            role="button"
            aria-label="Close"
          >
            X
          </a>
        )}
        {children}
      </div>
    </div>
  );
};

function handleChildClick(e: MouseEvent<HTMLDivElement, MouseEvent>) {
  e.stopPropagation();
}

export default Popup;
